import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

/**
 * Deep Link Handler Component
 *
 * Checks for pending deep links (set by native app URL open events)
 * and navigates to the appropriate route.
 *
 * This handles QR code scanning for gym timer sessions.
 */
const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    logger.info('📲 DeepLinkHandler mounted and ready');

    // Check for pending deep link on mount and periodically
    const checkPendingDeepLink = () => {
      if (typeof window !== 'undefined' && (window as any).__pendingDeepLink) {
        const deepLink = (window as any).__pendingDeepLink;
        logger.info('📲 Found pending deep link, navigating to:', deepLink);

        // Clear the pending deep link
        delete (window as any).__pendingDeepLink;

        // Navigate to the deep link path
        navigate(deepLink);
        logger.info('📲 Navigation triggered to:', deepLink);
      }
    };

    // Listen for custom deeplink events
    const handleDeepLinkEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.path) {
        logger.info('📲 Received deeplink event, navigating to:', customEvent.detail.path);
        navigate(customEvent.detail.path);
        logger.info('📲 Navigation triggered via event to:', customEvent.detail.path);
      }
    };

    // Add event listener
    window.addEventListener('deeplink', handleDeepLinkEvent);
    logger.info('📲 Added deeplink event listener');

    // Check immediately
    checkPendingDeepLink();

    // Check periodically in case deep link was set before this component mounted
    const interval = setInterval(checkPendingDeepLink, 500);
    logger.info('📲 Started periodic check for pending deep links');

    // Cleanup interval after 5 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      logger.info('📲 Stopped periodic check after 5 seconds');
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('deeplink', handleDeepLinkEvent);
      logger.info('📲 DeepLinkHandler cleanup complete');
    };
  }, [navigate]);

  return null; // This component doesn't render anything
};

export default DeepLinkHandler;
